/**
 * MetabolicSubphenotypePanel — Clinician-only card for OGTT pattern analysis.
 *
 * Lets the clinician enter a 7-point OGTT glucose curve (+ optional HOMA-IR /
 * HOMA-β) and receive a pattern-analysis result mapped to four metabolic
 * dysregulation axes (muscle IR, β-cell dysfunction, hepatic IR, incretin).
 *
 * Gated by the backend `require_clinician_plan`; non-clinicians never see
 * this panel (parent page checks subscription before rendering).
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Activity, Loader2, Info } from "lucide-react";

const SAMPLE_TIMES = [0, 30, 60, 90, 120, 150, 180];

const AXIS_META = {
  muscle_ir:  { bg: "Мускулна инсулинова резистентност", en: "Muscle IR",       color: "#F97316" },
  beta_cell:  { bg: "β-клетъчна дисфункция",              en: "β-cell",          color: "#EF4444" },
  hepatic_ir: { bg: "Чернодробна инсулинова резистентност", en: "Hepatic IR",   color: "#8B5CF6" },
  incretin:   { bg: "Инкретиново нарушение",              en: "Incretin defect", color: "#0EA5E9" },
};

export default function MetabolicSubphenotypePanel() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [glucose, setGlucose] = useState(["", "", "", "", "", "", ""]);
  const [homaIR, setHomaIR] = useState("");
  const [homaBeta, setHomaBeta] = useState("");
  const [patientId, setPatientId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const setG = (idx, v) => setGlucose((arr) => { const n = [...arr]; n[idx] = v; return n; });

  const run = async () => {
    const curve = glucose.map((v) => (v === "" ? null : Number(v)));
    if (curve[0] === null || curve[4] === null) {
      toast.error(bg ? "Задължителни: t=0 и t=120 мин глюкоза." : "Required: t=0 and t=120 min glucose.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/clinical/metabolic-subphenotype`, {
        glucose_mmol_l: curve,
        homa_ir: homaIR === "" ? null : Number(homaIR),
        homa_beta: homaBeta === "" ? null : Number(homaBeta),
        patient_id: patientId || null,
        lang: bg ? "bg" : "en",
      });
      setResult(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error(e.response.data.detail);
      } else {
        toast.error(e?.response?.data?.detail || (bg ? "Грешка при анализ" : "Analysis error"));
      }
    } finally {
      setLoading(false);
    }
  };

  const cardCls = isDark ? "bg-[hsl(223,47%,11%)] border border-white/5" : "bg-white border border-gray-100 shadow-sm";
  const inputCls = isDark ? "bg-[hsl(223,47%,14%)] border-[hsl(223,47%,18%)]" : "";

  return (
    <Card className={cardCls} data-testid="metabolic-subphenotype-panel">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-1">
          <Activity className="w-5 h-5 text-purple-500" />
          <h3 className="text-base sm:text-lg font-semibold">
            {bg ? "Метаболитен subphenotype анализ (OGTT)" : "Metabolic subphenotype analysis (OGTT)"}
          </h3>
          <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-700 dark:text-purple-300 font-semibold">
            CLINICIAN
          </span>
        </div>
        <p className={`text-xs mb-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {bg
            ? "Stanford Metabolic Subphenotype Predictor (Metwally 2024). Въведете 7-точков OGTT за откриване на модели на метаболитна дисрегулация."
            : "Stanford Metabolic Subphenotype Predictor (Metwally 2024). Enter a 7-point OGTT to detect metabolic dysregulation patterns."}
        </p>

        {/* Glucose curve inputs — vertical list so each field is usable
            even inside the narrow side panel. */}
        <div className="space-y-1.5 mb-4">
          <div className={`text-[10px] mb-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            {bg ? "Глюкоза (mmol/L)" : "Glucose (mmol/L)"}
          </div>
          {SAMPLE_TIMES.map((t, i) => (
            <div key={t} className="flex items-center gap-2">
              <label
                htmlFor={`msp-glucose-${t}-input`}
                className={`text-[11px] font-mono w-14 flex-shrink-0 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                t={t} мин
              </label>
              <Input
                id={`msp-glucose-${t}-input`}
                type="number"
                step="0.1"
                placeholder={i === 0 ? "напр. 5.2" : ""}
                value={glucose[i]}
                onChange={(e) => setG(i, e.target.value)}
                className={`h-9 text-sm ${inputCls}`}
                data-testid={`msp-glucose-${t}`}
                aria-label={`Glucose at t=${t} min`}
              />
            </div>
          ))}
        </div>

        {/* Optional HOMA + patient id — single-column so they also fit */}
        <div className="space-y-2 mb-4">
          <div>
            <label className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>HOMA-IR ({bg ? "опц." : "opt."})</label>
            <Input type="number" step="0.1" value={homaIR} onChange={(e) => setHomaIR(e.target.value)} className={`h-9 ${inputCls}`} data-testid="msp-homa-ir" />
          </div>
          <div>
            <label className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>HOMA-β ({bg ? "опц." : "opt."})</label>
            <Input type="number" step="0.1" value={homaBeta} onChange={(e) => setHomaBeta(e.target.value)} className={`h-9 ${inputCls}`} data-testid="msp-homa-beta" />
          </div>
          <div>
            <label className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>{bg ? "Пациент ID (опц.)" : "Patient ID (opt.)"}</label>
            <Input value={patientId} onChange={(e) => setPatientId(e.target.value)} className={`h-9 ${inputCls}`} data-testid="msp-patient-id" />
          </div>
        </div>

        <Button
          onClick={run}
          disabled={loading}
          className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white"
          data-testid="msp-analyze-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {bg ? "Анализирай" : "Analyze"}
        </Button>

        {result && (
          <div className="mt-5 space-y-4" data-testid="msp-result">
            <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {bg ? "Доминантен модел:" : "Primary pattern:"}{" "}
              <span className="font-semibold" data-testid="msp-primary">
                {result.primary_pattern === "none"
                  ? (bg ? "няма изразен модел" : "no dominant pattern")
                  : (bg ? AXIS_META[result.primary_pattern].bg : AXIS_META[result.primary_pattern].en)}
              </span>
            </div>

            <div className="space-y-2">
              {Object.entries(result.scores).map(([key, score]) => {
                const meta = AXIS_META[key];
                const pct = Math.round(score * 100);
                return (
                  <div key={key} data-testid={`msp-score-${key}`}>
                    <div className="flex items-center justify-between mb-0.5 text-xs">
                      <span>{bg ? meta.bg : meta.en}</span>
                      <span className="font-mono font-semibold" style={{ color: meta.color }}>
                        {pct}%
                      </span>
                    </div>
                    <div className={`h-1.5 rounded-full ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
                      <div className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: meta.color }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={`rounded-lg p-3 text-xs leading-relaxed ${isDark ? "bg-purple-500/10 text-purple-100" : "bg-purple-50 text-purple-900"}`}
              data-testid="msp-clinical-note">
              <Info className="inline w-3.5 h-3.5 mr-1 align-text-bottom" />
              {result.clinical_note}
            </div>

            <details className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              <summary className="cursor-pointer select-none">
                {bg ? "Извлечени характеристики на кривата" : "Extracted curve features"}
              </summary>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 font-mono">
                {Object.entries(result.features).map(([k, v]) => (
                  <div key={k} className={`px-2 py-1 rounded ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                    <span className="opacity-60">{k}:</span> {v}
                  </div>
                ))}
              </div>
            </details>

            <div className={`text-[10px] italic ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {result.framing} · {result.method}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
